import styles from "./Sparkline.module.scss";

export interface SparklineProps {
  /** The ordered series (oldest → newest). Fewer than 2 finite points renders a flat baseline (born-blank) —
   * NEVER a fabricated trend curve (the leak-test contract: a chart of no data shows no trend, not a made-up one). */
  values: Array<number | null | undefined>;
  /** SVG viewport width (px). */
  width?: number;
  /** SVG viewport height (px). */
  height?: number;
  /** Fill the area under the line. */
  area?: boolean;
  /** A brand tone for the line color (e.g. "spotify"); defaults to the app accent. */
  tone?: string;
  /** Accessible label for the trend (rendered as `role="img"`). */
  label?: string;
}

/**
 * A sparkline / area trend chart (Sparkline — "+projection"/area variant). A hand-rolled single-hue SVG
 * polyline over an ordered numeric series, on-token (stroke = `currentColor`, driven by a design token in SCSS).
 * With fewer than two finite points it renders a flat baseline — the born-blank empty state — rather than
 * inventing a curve.
 */
export function Sparkline({ values, width = 120, height = 32, area = false, tone, label }: SparklineProps) {
  const pad = 2;
  const finite = values.filter((v): v is number => v != null && Number.isFinite(v));
  const hasTrend = finite.length >= 2;

  let linePath = "";
  let areaPath = "";
  if (hasTrend) {
    const min = Math.min(...finite);
    const max = Math.max(...finite);
    const span = max - min || 1;
    const n = finite.length;
    const coords = finite.map((v, i) => {
      const x = pad + (i / (n - 1)) * (width - pad * 2);
      const y = height - pad - ((v - min) / span) * (height - pad * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    });
    linePath = coords.map((c, i) => `${i === 0 ? "M" : "L"}${c}`).join(" ");
    const baseline = (height - pad).toFixed(1);
    const firstX = (pad).toFixed(1);
    const lastX = (width - pad).toFixed(1);
    areaPath = `M${firstX},${baseline} ${coords.map((c) => `L${c}`).join(" ")} L${lastX},${baseline} Z`;
  }

  return (
    <svg
      className={styles.sparkline}
      data-tone={tone}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={label ?? "trend"}
      preserveAspectRatio="none"
    >
      {hasTrend ? (
        <>
          {area && <path className={styles.area} d={areaPath} />}
          <path className={styles.line} d={linePath} fill="none" />
        </>
      ) : (
        <line className={styles.baseline} x1={pad} y1={height / 2} x2={width - pad} y2={height / 2} />
      )}
    </svg>
  );
}
